/* eslint-disable @typescript-eslint/no-unsafe-return */
import { createSelector } from 'reselect/lib';

import { EnumAppSignatureType, EnumAppSubmitted, MAppMeta, MAppSignature, MProfile } from './scholarship.models';
import { IMatchApplicationsState } from './scholarship.state';

interface IRootState {
  scholarship: IMatchApplicationsState;
}

const selectMatchState: (state: IRootState) => IMatchApplicationsState = (state) => state.scholarship;

export const selectApplicationCount = createSelector([selectMatchState], (scholarship): number => scholarship.applicationsCount);

export const selectApplication = createSelector([selectMatchState], (scholarship) => scholarship.application);

export const selectApplicationId = createSelector([selectMatchState], (scholarship) => scholarship.applicationId);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const selectApplicationFromApplications = (id: string) =>
  createSelector([selectMatchState], (scholarship): MAppMeta | undefined =>
    scholarship.applications?.find((item: MAppMeta) => item.id === id)
  );

export const selectApplications = createSelector([selectMatchState], (scholarship): MAppMeta[] | undefined =>
  scholarship.applications ? scholarship.applications : undefined
);

export const selectApplicationsInProgress = createSelector([selectMatchState], (scholarship): MAppMeta[] =>
  scholarship.applications ? scholarship.applications.filter((item: MAppMeta) => item.submitted === EnumAppSubmitted.NO) : []
);

export const selectApplicationsCompleted = createSelector([selectMatchState], (scholarship): MAppMeta[] =>
  scholarship.applications ? scholarship.applications.filter((item: MAppMeta) => item.submitted === EnumAppSubmitted.YES) : []
);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const selectApplicationSection = (id: string) =>
  createSelector([selectMatchState], (scholarship): MAppMeta | undefined =>
    scholarship.applications?.find((item: MAppMeta) => item.id === id)
  );

export const selectError = createSelector([selectMatchState], (scholarship): string | undefined => scholarship.error);

export const selectEssay = createSelector([selectMatchState], (scholarship): unknown[] =>
  scholarship.application ? [scholarship.application.essay] : []
);

export const selectTranscript = createSelector([selectMatchState], (scholarship): unknown[] =>
  scholarship.application ? [scholarship.application.transcript] : []
);

export const selectIsSavingProfile = createSelector([selectMatchState], (scholarship): boolean => scholarship.savingProfile);

export const selectIsSavingApplication = createSelector([selectMatchState], (scholarship): boolean => scholarship.savingApplication);

export const selectIsSavingPurchaser = createSelector([selectMatchState], (scholarship): boolean => scholarship.savingPurchaser);

export const selectIsLoading = createSelector([selectMatchState], (scholarship): boolean | undefined => scholarship.isLoading);

export const selectLoadingApplications = createSelector(
  [selectMatchState],
  (scholarship): boolean | undefined => scholarship.loadingApplications
);

export const selectLinkedChildProfile = createSelector([selectMatchState], (scholarship): unknown =>
  scholarship.application?.meta ? scholarship.application.meta.linkedChildProfile : undefined
);

export const selectMatchLoading = createSelector([selectMatchState], (scholarship): boolean | undefined => scholarship.loadingMatch);

export const selectMatchLazyLoading = createSelector([selectMatchState], (scholarship): boolean | undefined => scholarship.lazyLoading);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const selectProfile = (id: string) =>
  createSelector([selectMatchState], (scholarship): MProfile | undefined => scholarship.profiles?.find((item: MProfile) => item.id === id));

export const selectProfilesParent = createSelector([selectMatchState], (scholarship): MProfile[] =>
  scholarship.profiles ? scholarship.profiles.filter((item: MProfile) => !!item.relationship) : []
);

export const selectProfilesChild = createSelector([selectMatchState], (scholarship): MProfile[] =>
  scholarship.profiles ? scholarship.profiles.filter((item: MProfile) => item.relationship === undefined) : []
);

export const selectProfileAny = createSelector([selectMatchState], (scholarship): MProfile | undefined =>
  scholarship?.profiles ? scholarship.profiles[0] : undefined
);

export const selectEssayDocContent = createSelector([selectMatchState], (scholarship): unknown =>
  scholarship.application?.essay ? scholarship.application.essay.docContent : undefined
);

export const selectTranscriptDocContent = createSelector([selectMatchState], (scholarship): unknown =>
  scholarship.application?.transcript ? scholarship.application.transcript.docContent : undefined
);

export const selectSignatureParent = createSelector([selectMatchState], (scholarship): MAppSignature[] =>
  scholarship.application
    ? scholarship.application.signatures.filter((item: MAppSignature) => item.signatureType === EnumAppSignatureType.PARENT)
    : []
);

export const selectSignatureParentIndex = createSelector([selectMatchState], (scholarship): number =>
  scholarship.application?.signatures
    ? scholarship.application.signatures.findIndex((item: MAppSignature) => item.signatureType === EnumAppSignatureType.PARENT)
    : -1
);

export const selectSignatureAgreement = createSelector([selectMatchState], (scholarship): MAppSignature | undefined =>
  scholarship.application?.signatures
    ? scholarship.application.signatures.find((item: MAppSignature) => item.signatureType === EnumAppSignatureType.AGREEMENT)
    : undefined
);

export const selectSignatureCertification = createSelector([selectMatchState], (scholarship): MAppSignature | undefined =>
  scholarship.application?.signatures
    ? scholarship.application.signatures.find((item: MAppSignature) => item.signatureType === EnumAppSignatureType.CERTIFICATION)
    : undefined
);

export const selectSignaturesChild = createSelector([selectMatchState], (scholarship): MAppSignature[] =>
  scholarship.signatures ? scholarship.signatures.filter((item: MAppSignature) => item.signatureType === EnumAppSignatureType.CHILD) : []
);

// Only ever have one purchaser. Switched to selectSignaturePurhaser
// export const selectSignaturesPurchaser = createSelector([selectMatchState], (scholarship) =>
//   scholarship.application
//     ? scholarship.application.signatures.filter((item: MAppSignature) => item.signatureType === EnumAppSignatureType.PURCHASER)
//     : []
// );

export const selectSignaturePurchaser = createSelector([selectMatchState], (scholarship): MAppSignature | undefined =>
  scholarship.application?.signatures
    ? scholarship.application.signatures.find((item: MAppSignature) => item.signatureType === EnumAppSignatureType.PURCHASER)
    : undefined
);

export const selectStep = createSelector([selectMatchState], (scholarship): number => scholarship.step);

export const selectSteps = createSelector([selectMatchState], (scholarship): unknown[] => scholarship.steps);

export const selectStepsCompleted = createSelector(
  [selectMatchState],
  (scholarship): Record<number, boolean> => scholarship.stepsCompleted
);

export const selectResendEmailStatus = createSelector(
  [selectMatchState],
  (scholarship): string | undefined => scholarship.resendEmailStatus
);

export const selectSendingEmail = createSelector([selectMatchState], (scholarship): boolean | undefined => scholarship.sendingEmail);

export const selectUnifiedError = (state: IRootState): string | undefined => state.scholarship.error || state.scholarship.addPurchaserError;
